// ./src/components/generic/AppDrawer/AppDrawer.tsx
import React from "react";
import { Drawer, Text, Group, Divider, Box, Alert } from "@mantine/core";
import { AppButton } from "../AppButton";
import { AppIcon } from "../AppIcon";

export interface AppDrawerAction {
  /** Button label */
  label: string;

  /** Button variant */
  variant?: "primary" | "secondary" | "tertiary" | "danger";

  /** Button icon */
  iconName?: string;

  /** Click handler */
  onClick: () => void;

  /** Loading state */
  inProgress?: boolean;

  /** Disabled state */
  disabled?: boolean;
}

export interface AppDrawerProps {
  /** Whether the drawer is open */
  opened: boolean;

  /** Function to close the drawer */
  onClose: () => void;

  /** Confirm action callback */
  onConfirm?: () => void;

  /** Drawer title */
  title: string;

  /** Optional description/subtitle */
  description?: string;

  /** Drawer content */
  children: React.ReactNode;

  /** Array of action buttons (right-aligned) */
  actions?: AppDrawerAction[];

  /** Array of left-aligned action buttons */
  leftActions?: AppDrawerAction[];

  /** Custom React node to render alongside right-aligned actions */
  customActions?: React.ReactNode;

  /** Quick setup for common button combinations */
  actionType?: "ok" | "close" | "confirm-cancel" | "save-cancel" | "custom";

  /** Size of the drawer (width for right/left, height for top/bottom) */
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "full" | string | number;

  /** Position of the drawer */
  position?: "right" | "left" | "top" | "bottom";

  /** Whether drawer can be closed by clicking outside or pressing escape */
  closable?: boolean;

  /** Loading state that affects all interactions */
  loading?: boolean;

  /** Additional className for styling */
  className?: string;

  /** Additional styles */
  style?: React.CSSProperties;

  /** Custom footer content (overrides action buttons) */
  footer?: React.ReactNode;

  /** Whether to show divider between content and footer */
  withDivider?: boolean;

  /** Error message(s) to display above actions */
  errors?: string | string[] | React.ReactNode;
}

export const AppDrawer: React.FC<AppDrawerProps> = ({
  opened,
  onClose,
  onConfirm,
  title,
  description,
  children,
  actions,
  leftActions,
  customActions,
  actionType = "custom",
  size = "lg",
  position = "right",
  closable = true,
  loading = false,
  className,
  style,
  footer,
  withDivider = true,
  errors,
}) => {
  // Generate default actions based on actionType
  const getDefaultActions = (): AppDrawerAction[] => {
    switch (actionType) {
      case "ok":
        return [
          {
            label: "OK",
            variant: "primary",
            onClick: onClose,
          },
        ];

      case "close":
        return [
          {
            label: "Close",
            variant: "secondary",
            onClick: onClose,
          },
        ];

      case "confirm-cancel":
        return [
          {
            label: "Cancel",
            variant: "secondary",
            onClick: onClose,
          },
          {
            label: "Confirm",
            variant: "primary",
            onClick: onConfirm ?? onClose,
          },
        ];

      case "save-cancel":
        return [
          {
            label: "Cancel",
            variant: "secondary",
            onClick: onClose,
          },
          {
            label: "Save",
            variant: "primary",
            iconName: "Check",
            onClick: onConfirm ?? onClose,
          },
        ];

      default:
        return [];
    }
  };

  const finalActions = actions || getDefaultActions();
  const canClose = closable && !loading;

  const handleClose = () => {
    if (canClose) {
      onClose();
    }
  };

  const renderErrors = () => {
    if (!errors) return null;

    // If errors is a React node, render it directly
    if (React.isValidElement(errors)) {
      return <Box px="md">{errors}</Box>;
    }

    // Handle string or array of strings
    const errorArray = Array.isArray(errors) ? errors : [errors];

    if (errorArray.length === 0) return null;

    return (
      <Box px="md">
        <Alert
          color="red"
          icon={<AppIcon iconName="AlertCircle" size={16} />}
          styles={{
            root: {
              padding: "0.75rem",
            },
          }}
        >
          {errorArray.length === 1 ? (
            <Text size="sm">{errorArray[0]}</Text>
          ) : (
            <Box component="ul" style={{ margin: 0, paddingLeft: "1.25rem" }}>
              {errorArray.map((error, index) => (
                <li key={index}>
                  <Text size="sm">{error}</Text>
                </li>
              ))}
            </Box>
          )}
        </Alert>
      </Box>
    );
  };

  const renderActions = () => {
    if (footer) {
      return footer;
    }

    const hasLeftActions = leftActions && leftActions.length > 0;
    const hasRightActions = finalActions.length > 0;
    const hasCustomActions = !!customActions;

    if (!hasLeftActions && !hasRightActions && !hasCustomActions) {
      return null;
    }

    return (
      <>
        {withDivider && <Divider />}
        <Group justify="space-between" gap="sm" p="md">
          {/* Left-aligned actions */}
          {hasLeftActions && (
            <Group gap="sm">
              {leftActions.map((action, index) => (
                <AppButton
                  key={index}
                  variant={action.variant || "secondary"}
                  iconName={action.iconName}
                  onClick={action.onClick}
                  inProgress={action.inProgress || loading}
                  disabled={action.disabled || loading}
                >
                  {action.label}
                </AppButton>
              ))}
            </Group>
          )}

          {/* Right-aligned actions (standard buttons + custom actions) */}
          {(hasRightActions || hasCustomActions) && (
            <Group
              gap="sm"
              style={{ marginLeft: hasLeftActions ? undefined : "auto" }}
            >
              {finalActions.map((action, index) => (
                <AppButton
                  key={index}
                  variant={action.variant || "secondary"}
                  iconName={action.iconName}
                  onClick={action.onClick}
                  inProgress={action.inProgress || loading}
                  disabled={action.disabled || loading}
                >
                  {action.label}
                </AppButton>
              ))}
              {customActions}
            </Group>
          )}
        </Group>
      </>
    );
  };

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      title={title}
      size={size}
      position={position}
      closeOnClickOutside={canClose}
      closeOnEscape={canClose}
      withCloseButton={canClose}
      {...(className && { className })}
      styles={{
        body: {
          padding: 0,
          height: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
        content: {
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          ...style,
        },
      }}
    >
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        {/* Description Section (if present) - Fixed */}
        {description && (
          <Box px="md" pt="md" pb="sm" style={{ flexShrink: 0 }}>
            <Text size="md" c="dimmed">
              {description}
            </Text>
          </Box>
        )}

        {/* Scrollable Content */}
        <Box
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            minHeight: 0,
          }}
          px="md"
          py={description ? "sm" : "md"}
        >
          {children}
        </Box>

        {/* Error Section (if present) - Fixed */}
        {errors && (
          <Box py="md" style={{ flexShrink: 0 }}>
            {renderErrors()}
          </Box>
        )}

        {/* Footer - Fixed */}
        <Box style={{ flexShrink: 0 }}>{renderActions()}</Box>
      </Box>
    </Drawer>
  );
};
